import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import { ContentSpacer } from "../common/layout";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useIsDemoOrg } from "../../lib/use-is-demo-org";
import { mockProviders, ProviderCategory } from "./mock-providers";
import { useMockConnections } from "./use-mock-connections";
import { ProviderCard } from "./components/provider-card";

type CategoryFilter = "all" | ProviderCategory;

const categoryTabs: { value: CategoryFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "communication", label: "Communication" },
    { value: "project_management", label: "Project Management" },
    { value: "crm", label: "CRM" },
    { value: "productivity", label: "Productivity" },
];

export function IntegrationsPage() {
    const isDemoOrg = useIsDemoOrg();
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
    const { connect, disconnect, getStatus } = useMockConnections();

    const filtered = useMemo(() => {
        return mockProviders.filter((p) => {
            if (activeCategory !== "all" && p.category !== activeCategory) return false;
            if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    }, [search, activeCategory]);

    if (!isDemoOrg) {
        return <Navigate to="/projects" replace />;
    }

    return (
        <ContentSpacer contentWidth="wider">
            <div className="mt-8 flex flex-col gap-1">
                <div className="flex items-center gap-3">
                    <h2>Integrations</h2>
                    <Badge variant="secondary">600+ available</Badge>
                </div>
                <p className="text-muted-foreground">
                    Connect your favorite tools to automate workflows and sync data across your stack.
                </p>
            </div>

            <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    placeholder="Search integrations..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-4 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
            </div>

            <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as CategoryFilter)}>
                <TabsList>
                    {categoryTabs.map((tab) => (
                        <TabsTrigger key={tab.value} value={tab.value}>
                            {tab.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            <p className="text-sm text-muted-foreground">
                {filtered.length} integration{filtered.length !== 1 ? "s" : ""}
            </p>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((provider) => (
                    <ProviderCard
                        key={provider.id}
                        provider={provider}
                        status={getStatus(provider.id)}
                        onConnect={() => connect(provider.id)}
                        onDisconnect={() => disconnect(provider.id)}
                    />
                ))}
            </div>
        </ContentSpacer>
    );
}
